/* eslint-disable no-console */
import { useState, useEffect } from "react";
import { Button, Form, Icon, Message, Checkbox } from "semantic-ui-react";
import moment from "moment";
import { Attendance } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { CustomTable } from "../../../Shared";
import "./MassiveAttendanceForm.scss";

const attendanceController = new Attendance();

// Constantes del componente
const ATTENDANCE_OPTIONS = [
  { key: "seleccione", text: "-- Seleccione --", value: "", color: "grey" },
  { key: "presente", text: "Presente", value: "presente", color: "green" },
  { key: "tardanza", text: "Tardanza", value: "tardanza", color: "yellow" },
  { key: "ausente", text: "Ausente", value: "ausente", color: "red" },
  {
    key: "justificado",
    text: "Falta Justificada",
    value: "justificado",
    color: "orange",
  },
];

const DEFAULT_TIMES = {
  presente: "07:30",
  tardanza: "07:45",
  default: "07:30",
};

// Fecha de hoy según el reloj local del navegador, normalizada a UTC sin hora
// (evita que moment.utc() sin argumentos devuelva el día siguiente cuando el
// offset local ya cruzó la medianoche UTC, ej. después de las 19:00 en Perú)
const getTodayUtc = () => moment.utc(moment().format("YYYY-MM-DD"));

export function MassiveAttendanceForm(props) {
  const {
    onClose,
    onReload,
    students,
    gradeName,
    gradeId,
    academicCycleId,
    academicCycle,
  } = props;
  const { accessToken, user } = useAuth();

  const cycleStartMoment = academicCycle?.startDate
    ? moment.utc(academicCycle.startDate)
    : null;
  const cycleEndMoment = academicCycle?.endDate
    ? moment.utc(academicCycle.endDate)
    : null;

  // Función para validar que el día sea de lunes a viernes
  const isWeekday = (dateMoment) => {
    const dayOfWeek = dateMoment.day(); // 0 = Domingo, 6 = Sábado
    return dayOfWeek >= 1 && dayOfWeek <= 5; // Lunes a Viernes
  };

  // Función para obtener el siguiente día hábil (lunes a viernes)
  const getNextWeekday = (dateMoment) => {
    let result = dateMoment.clone();
    while (!isWeekday(result)) {
      result.add(1, 'day');
    }
    return result;
  };

  const clampDateToCycle = (dateMoment) => {
    let result = dateMoment ? dateMoment.clone() : getTodayUtc();

    if (cycleStartMoment && result.isBefore(cycleStartMoment, "day")) {
      result = cycleStartMoment.clone();
    }

    if (cycleEndMoment && result.isAfter(cycleEndMoment, "day")) {
      result = cycleEndMoment.clone();
    }

    // Asegurar que sea un día hábil (lunes a viernes)
    if (!isWeekday(result)) {
      result = getNextWeekday(result);
      // Verificar nuevamente que esté dentro del ciclo después de ajustar
      if (cycleEndMoment && result.isAfter(cycleEndMoment, "day")) {
        // Si el siguiente día hábil está fuera del ciclo, buscar el día hábil anterior
        result = dateMoment.clone();
        while (!isWeekday(result) && result.isAfter(cycleStartMoment)) {
          result.subtract(1, 'day');
        }
      }
    }

    return result;
  };

  const defaultAttendanceDate = clampDateToCycle(getTodayUtc()).format("YYYY-MM-DD");
  const cycleStartDate = cycleStartMoment
    ? cycleStartMoment.format("YYYY-MM-DD")
    : undefined;
  const cycleEndDate = cycleEndMoment
    ? cycleEndMoment.format("YYYY-MM-DD")
    : undefined;

  const [formData, setFormData] = useState({
    attendanceDate: defaultAttendanceDate,
    attendanceRecords: students.map((student) => ({
      studentId: student.id,
      studentName: `${student.User.lastname} ${student.User.secondLastname} ${student.User.firstname}`,
      dni: student.User.dni,
      status: "", // default to empty - user must select
      arrivalTime: DEFAULT_TIMES.default, // default arrival time
      notes: "",
      isSelected: false, // Para el checkbox
      existingAttendanceId: null, // Para identificar si ya existe registro
      hasExistingRecord: false, // Para saber si el estudiante ya tiene asistencia registrada
    })),
  });

  const [loading, setLoading] = useState(false);
  const [loadingInitialData, setLoadingInitialData] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);

  const attendanceOptions = ATTENDANCE_OPTIONS;

  // Función para cargar asistencia existente
  const loadExistingAttendance = async (date) => {
    setLoadingInitialData(true);
    const parsedDate = moment.utc(date, "YYYY-MM-DD", true);
    const normalizedMoment = clampDateToCycle(
      parsedDate.isValid() ? parsedDate : getTodayUtc()
    );
    const normalizedDate = normalizedMoment.format("YYYY-MM-DD");

    try {
      const response =
        await attendanceController.getDailyAttendanceByGradeAndDate(
          accessToken,
          gradeId,
          normalizedDate,
          academicCycleId
        );

      const existingAttendanceMap = {};
      if (response.data && response.data.length > 0) {
        response.data.forEach((attendance) => {
          existingAttendanceMap[attendance.studentId] = attendance;
        });
      }

      // Actualizar los registros con la información existente
      setFormData((prev) => ({
        ...prev,
        attendanceDate: normalizedDate,
        attendanceRecords: prev.attendanceRecords.map((record) => {
          const existingRecord = existingAttendanceMap[record.studentId];
          if (existingRecord) {
            return {
              ...record,
              status: existingRecord.status,
              arrivalTime: existingRecord.arrivalTime
                ? existingRecord.arrivalTime.substring(0, 5) // Convert HH:MM:SS to HH:MM for input
                : DEFAULT_TIMES.default,
              notes: existingRecord.notes || "",
              isSelected: false, // No marcar automáticamente aunque exista registro
              existingAttendanceId: existingRecord.id,
              hasExistingRecord: true,
            };
          }
          return {
            ...record,
            status: "", // Reset to empty - user must select
            arrivalTime: DEFAULT_TIMES.default, // Reset to default time
            notes: "", // Reset notes
            isSelected: false,
            existingAttendanceId: null,
            hasExistingRecord: false,
          };
        }),
      }));
    } catch (error) {
      // Si no hay registros existentes, reiniciar todo
      setFormData((prev) => ({
        ...prev,
        attendanceDate: normalizedDate,
        attendanceRecords: prev.attendanceRecords.map((record) => ({
          ...record,
          status: "", // Reset to empty - user must select
          arrivalTime: DEFAULT_TIMES.default,
          notes: "",
          isSelected: false,
          existingAttendanceId: null,
          hasExistingRecord: false,
        })),
      }));
    } finally {
      setLoadingInitialData(false);
    }
  };

  // UseEffect para cargar datos iniciales
  useEffect(() => {
    loadExistingAttendance(defaultAttendanceDate);
  }, [
    gradeId,
    academicCycleId,
    cycleStartDate,
    cycleEndDate,
    defaultAttendanceDate,
  ]);

  const handleDateChange = (e) => {
    const rawValue = e.target.value;
    const parsedDate = moment.utc(rawValue, "YYYY-MM-DD", true);
    
    if (!parsedDate.isValid()) {
      setError("Por favor, seleccione una fecha válida");
      return;
    }

    // Validar que sea día de semana (lunes a viernes)
    if (!isWeekday(parsedDate)) {
      setError("Solo se puede registrar asistencia de lunes a viernes");
      return;
    }

    const normalizedDate = clampDateToCycle(parsedDate).format("YYYY-MM-DD");
    clearErrors(); // Limpiar errores al cambiar fecha válida
    loadExistingAttendance(normalizedDate);
  };

  const handleStudentAttendanceChange = (studentIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      attendanceRecords: prev.attendanceRecords.map((record, index) =>
        index === studentIndex ? { ...record, [field]: value } : record
      ),
    }));
  };

  const handleStudentSelectionChange = (studentIndex, isSelected) => {
    setFormData((prev) => ({
      ...prev,
      attendanceRecords: prev.attendanceRecords.map((record, index) =>
        index === studentIndex ? { ...record, isSelected } : record
      ),
    }));
  };

  const setAllStudentsSelection = (isSelected) => {
    setFormData((prev) => ({
      ...prev,
      attendanceRecords: prev.attendanceRecords.map((record) => ({
        ...record,
        isSelected,
      })),
    }));
  };

  const setAllStudentsStatus = (status) => {
    clearErrors(); // Limpiar errores al cambiar estado masivamente
    setFormData((prev) => ({
      ...prev,
      attendanceRecords: prev.attendanceRecords.map((record) => ({
        ...record,
        status: status,
        arrivalTime:
          status === "tardanza"
            ? DEFAULT_TIMES.tardanza
            : DEFAULT_TIMES.default, // Set different time for late students
        isSelected: true, // Marcar todos como seleccionados cuando se cambia el estado masivamente
      })),
    }));
  };

  // Función para limpiar errores
  const clearErrors = () => {
    setError("");
    setValidationErrors([]);
  };

  // Función para parsear errores de validación del servidor
  const parseValidationErrors = (apiError) => {
    if (apiError.errors && Array.isArray(apiError.errors)) {
      return apiError.errors.map((error) => {
        // Extraer el índice del estudiante del path (ej: "attendances[4].status" -> 4)
        const pathMatch = error.path.match(/attendances\[(\d+)\]\.(.+)/);
        if (pathMatch) {
          const studentIndex = parseInt(pathMatch[1]);
          const field = pathMatch[2];
          const studentRecord = formData.attendanceRecords[studentIndex];

          return {
            ...error,
            studentIndex,
            field,
            studentName: studentRecord
              ? studentRecord.studentName
              : `Estudiante ${studentIndex + 1}`,
            studentDni: studentRecord ? studentRecord.dni : "",
          };
        }
        return error;
      });
    }
    return [];
  };

  // Función de validación del formulario
  const validateForm = () => {
    const selectedRecords = formData.attendanceRecords.filter(
      (record) => record.isSelected
    );

    if (selectedRecords.length === 0) {
      return "Debe seleccionar al menos un estudiante para registrar la asistencia";
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    clearErrors();

    // Validar formulario
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    try {
      const selectedRecords = formData.attendanceRecords.filter(
        (record) => record.isSelected
      );

      const massiveAttendanceData = {
        attendanceDate: formData.attendanceDate,
        academicCycleId: parseInt(academicCycleId),
        recordedBy: user.id, // User who is recording the attendance
        attendances: selectedRecords.map((record) => {
          const attendance = {
            studentId: record.studentId,
            status: record.status,
            notes: record.notes,
          };

          // Only include arrivalTime if student is not absent
          if (record.status !== "ausente") {
            // Ensure arrivalTime is in HH:MM:SS format
            attendance.arrivalTime = record.arrivalTime.includes(":")
              ? record.arrivalTime.length === 5
                ? `${record.arrivalTime}:00`
                : record.arrivalTime
              : `${record.arrivalTime}:00`;
          }

          // Si ya existe un registro, incluir el ID para actualización
          if (record.hasExistingRecord && record.existingAttendanceId) {
            attendance.attendanceId = record.existingAttendanceId;
          }

          return attendance;
        }),
      };

      await attendanceController.createMassiveAttendance(
        accessToken,
        gradeId,
        massiveAttendanceData
      );

      onReload();
      onClose();
    } catch (error) {
      // Verificar si es un error de validación del servidor
      if (error && error.message === "Errores de validación" && error.errors) {
        const parsedErrors = parseValidationErrors(error);
        setValidationErrors(parsedErrors);
      } else {
        // Error general
        setValidationErrors([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Componente para mostrar errores de validación detallados
  const ValidationErrorsDisplay = () => {
    if (validationErrors.length === 0) return null;

    return (
      <Message color="red" onDismiss={clearErrors}>
        <Message.Header>
          <Icon name="exclamation triangle" />
          Errores de validación encontrados
        </Message.Header>
        <Message.List>
          {validationErrors.map((error, index) => (
            <Message.Item key={index}>
              <strong>{error.studentName}</strong>
              {error.studentDni && ` (DNI: ${error.studentDni})`}
              {" - "}
              <span className="error-field">{error.field}:</span> {error.msg}
            </Message.Item>
          ))}
        </Message.List>
        <div style={{ marginTop: "10px", fontSize: "0.9em", color: "#666" }}>
          <Icon name="info circle" />
          Corrija los errores marcados en rojo en la tabla para continuar.
        </div>
      </Message>
    );
  };

  const getPresentCount = () =>
    formData.attendanceRecords.filter(
      (r) => r.isSelected && r.status === "presente"
    ).length;
  const getAbsentCount = () =>
    formData.attendanceRecords.filter(
      (r) => r.isSelected && r.status === "ausente"
    ).length;
  const getLateCount = () =>
    formData.attendanceRecords.filter(
      (r) => r.isSelected && r.status === "tardanza"
    ).length;
  const getJustifiedCount = () =>
    formData.attendanceRecords.filter(
      (r) => r.isSelected && r.status === "justificado"
    ).length;
  const getSelectedCount = () =>
    formData.attendanceRecords.filter((r) => r.isSelected).length;

  // Función para verificar si un estudiante tiene errores de validación
  const getStudentValidationErrors = (studentIndex) => {
    return validationErrors.filter(
      (error) => error.studentIndex === studentIndex
    );
  };

  // Función para verificar si un campo específico tiene errores
  const hasFieldError = (studentIndex, fieldName) => {
    return validationErrors.some(
      (error) =>
        error.studentIndex === studentIndex && error.field === fieldName
    );
  };

  // Funciones de renderizado de campos
  const renderStudentNameField = (record, index) => {
    const studentErrors = getStudentValidationErrors(index);
    const hasAnyError = studentErrors.length > 0;

    return (
      <div className="massive-attendance-form__student-name">
        <strong
          className={`student-name-text clickable-student-name ${
            hasAnyError ? "student-name-error" : ""
          }`}
          onClick={() => {
            if (!loadingInitialData) {
              handleStudentSelectionChange(index, !record.isSelected);
            }
          }}
          style={{ cursor: loadingInitialData ? "default" : "pointer" }}
        >
          {record.studentName}
        </strong>
        <div className="student-icons">
          {hasAnyError && (
            <Icon
              name="exclamation triangle"
              color="red"
              size="small"
              className="student-error-icon"
              title={`Errores de validación: ${studentErrors
                .map((e) => e.msg)
                .join(", ")}`}
            />
          )}
          {record.hasExistingRecord && (
            <Icon
              name="save"
              color="grey"
              title="Registro guardado"
              className="save-icon"
            />
          )}
        </div>
      </div>
    );
  };

  const renderStatusField = (record, index) => {
    const hasError = hasFieldError(index, "status");
    const studentErrors = getStudentValidationErrors(index);

    const getStatusClass = (status) => {
      const baseClass = "massive-attendance-form__input--status-select";
      let statusClass = status
        ? `${baseClass} status-${status}`
        : `${baseClass} status-default`;

      // Agregar clase de error si existe
      if (hasError) {
        statusClass += " status-error";
      }

      return statusClass;
    };

    const errorMessages = studentErrors
      .filter((error) => error.field === "status")
      .map((error) => error.msg)
      .join(", ");

    return (
      <div className="status-field-container">
        <select
          value={record.status}
          onChange={(e) => {
            const value = e.target.value;
            handleStudentAttendanceChange(index, "status", value);
            // Set default arrival time based on status
            if (value === "tardanza") {
              handleStudentAttendanceChange(
                index,
                "arrivalTime",
                DEFAULT_TIMES.tardanza
              );
            } else if (value === "presente") {
              handleStudentAttendanceChange(
                index,
                "arrivalTime",
                DEFAULT_TIMES.presente
              );
            }
            // Auto-seleccionar cuando se cambia el estado
            if (!record.isSelected) {
              handleStudentSelectionChange(index, true);
            }
            // Limpiar errores al cambiar el valor
            if (hasError) {
              clearErrors();
            }
          }}
          disabled={loadingInitialData}
          className={getStatusClass(record.status)}
          title={hasError ? errorMessages : ""}
        >
          {attendanceOptions.map((option) => (
            <option key={option.key} value={option.value}>
              {option.text}
            </option>
          ))}
        </select>
        {hasError && (
          <div className="field-error-message">
            <Icon name="exclamation triangle" color="red" size="small" />
            {errorMessages}
          </div>
        )}
      </div>
    );
  };

  const renderTimeField = (record, index) => {
    return (
      <input
        type="time"
        value={record.arrivalTime}
        onChange={(e) =>
          handleStudentAttendanceChange(index, "arrivalTime", e.target.value)
        }
        disabled={
          record.status === "ausente" ||
          record.status === "justificado" ||
          loadingInitialData
        }
      />
    );
  };

  const renderNotesField = (record, index) => (
    <input
      type="text"
      placeholder="Notas..."
      value={record.notes}
      onChange={(e) => {
        handleStudentAttendanceChange(index, "notes", e.target.value);
        // Auto-seleccionar cuando se agregan notas
        if (e.target.value && !record.isSelected) {
          handleStudentSelectionChange(index, true);
        }
      }}
      disabled={loadingInitialData}
    />
  );

  // Configuración para CustomTable
  const getTableConfig = () => ({
    headers: ["", "Estudiante", "DNI", "Estado", "Hora de Llegada", "Notas"],
    fields: [
      {
        component: "custom",
        align: "center",
        render: (record, index) => (
          <Checkbox
            checked={record.isSelected}
            onChange={(e, { checked }) =>
              handleStudentSelectionChange(index, checked)
            }
            disabled={loadingInitialData}
          />
        ),
      },
      {
        component: "custom",
        render: (record, index) => renderStudentNameField(record, index),
      },
      {
        field: "dni",
        dataType: "text",
      },
      {
        component: "custom",
        render: renderStatusField,
      },
      {
        component: "custom",
        render: renderTimeField,
      },
      {
        component: "custom",
        render: renderNotesField,
      },
    ],
  });

  return (
    <div className="massive-attendance-form">
      <Form onSubmit={handleSubmit}>
        {error && <Message negative content={error} />}
        <ValidationErrorsDisplay />
        <Form.Group widths="equal">
          <Form.Field>
            <Form.Input
              label="Fecha (Solo días hábiles: Lunes a Viernes)"
              type="date"
              value={formData.attendanceDate}
              onChange={handleDateChange}
              required
              disabled={loadingInitialData}
              min={cycleStartDate}
              max={cycleEndDate}
            />
          </Form.Field>
          <Form.Field>
            <label>Acciones Masivas</label>
            <Button.Group>
              <Button
                type="button"
                color="green"
                size="small"
                onClick={() => setAllStudentsStatus("presente")}
                disabled={loadingInitialData}
              >
                Todos Presentes
              </Button>
              <Button
                type="button"
                color="red"
                size="small"
                onClick={() => setAllStudentsStatus("ausente")}
                disabled={loadingInitialData}
              >
                Todos Ausentes
              </Button>
            </Button.Group>
          </Form.Field>
        </Form.Group>

        <div className="massive-attendance-form__table-container">
          {/* Header unificado con checkbox y resumen */}
          <div className="massive-attendance-form__header">
            <div className="massive-attendance-form__header-checkbox-section">
              <Checkbox
                checked={formData.attendanceRecords.every((r) => r.isSelected)}
                indeterminate={
                  formData.attendanceRecords.some((r) => r.isSelected) &&
                  !formData.attendanceRecords.every((r) => r.isSelected)
                }
                onChange={(e, { checked }) => setAllStudentsSelection(checked)}
                disabled={loadingInitialData}
              />
              <strong
                className="label-text clickable-label"
                onClick={() => {
                  if (!loadingInitialData) {
                    const allSelected = formData.attendanceRecords.every(
                      (r) => r.isSelected
                    );
                    setAllStudentsSelection(!allSelected);
                  }
                }}
                style={{ cursor: loadingInitialData ? "default" : "pointer" }}
              >
                Seleccionar todos los estudiantes
              </strong>
            </div>

            <div className="massive-attendance-form__header-summary">
              <span className="summary-item summary-item--selected">
                Seleccionados: {getSelectedCount()}
              </span>{" "}
              |
              <span className="summary-item summary-item--present">
                {" "}
                Presentes: {getPresentCount()}
              </span>{" "}
              |
              <span className="summary-item summary-item--absent">
                {" "}
                Ausentes: {getAbsentCount()}
              </span>{" "}
              |
              <span className="summary-item summary-item--late">
                {" "}
                Tardanzas: {getLateCount()}
              </span>{" "}
              |
              <span className="summary-item summary-item--justified">
                {" "}
                Justificados: {getJustifiedCount()}
              </span>
            </div>
          </div>

          <CustomTable
            tableConfig={getTableConfig()}
            data={formData.attendanceRecords}
            size="small"
            striped={true}
            loading={loadingInitialData}
            className="massive-attendance-table"
          />
        </div>

        <div className="massive-attendance-form__actions">
          <Button
            type="button"
            onClick={onClose}
            size="large"
            disabled={loading}
          >
            Cancelar
          </Button>

          <Button
            type="submit"
            primary
            loading={loading}
            size="large"
            disabled={getSelectedCount() === 0 || loadingInitialData}
          >
            <Icon name="save" />
            Registrar Asistencia de {getSelectedCount()} estudiante
            {getSelectedCount() !== 1 ? "s" : ""} seleccionado
            {getSelectedCount() !== 1 ? "s" : ""}
          </Button>
        </div>
      </Form>
    </div>
  );
}
